import React, { Fragment, useContext, useEffect, useMemo } from 'react'

import { Box, CellRenderers, ChannelCellRenderer, Loader, ProviderLogo, useAppTheme } from '@percept/mui'

import { DimensionalSummary } from 'components/DimensionalSummary'

import { PerformanceTotals } from './PerformanceTotals'

import { usePerformanceTimeseriesByProvider, usePlatformUnit } from '@percept/hooks'

import { PerformanceTableProps } from 'components/Tables'

import { useComparisonsByProvider } from 'hooks'

import { useProviderDatasets } from './perfUtils'

import { useChannelSummaries } from './lib'

import { DashboardContext } from 'contexts'

import { isLoading, isoDateUTC } from '@percept/utils'

import { getPotentialEfficiencyLabel } from 'components/Organisation'
import { resolvePerformanceReportingDimensions } from 'components/Organisation/lib'

import { channelDisplayMap, providerLabelMap } from '@percept/constants'

import {
  AnyPerformanceDimension,
  ChannelKey,
  PlatformUnit,
  PrimaryPerformanceDataProvider,
} from '@percept/types'

import { PerformanceReportingDatum } from 'types'
import { some } from 'lodash-es'


export type CrossChannelPerformanceSummaryProps = {
  platformUnit: PlatformUnit
  providers: PrimaryPerformanceDataProvider[]
  referenceDate: Date | null
}


const cellRenderers: CellRenderers<
  PerformanceReportingDatum & {
    channel: ChannelKey
    provider?: PrimaryPerformanceDataProvider
  }
> = {
  // eslint-disable-next-line react/display-name, react/prop-types
  label: ({ channel, provider }) => (
    provider ? (
      <ProviderLogo size={1.5} provider={provider} />
    ) : (
      <ChannelCellRenderer channel={channel} />
    )
  )
}



export const CrossChannelPerformanceSummary = ({
  platformUnit,
  providers,
  referenceDate,
}: CrossChannelPerformanceSummaryProps): JSX.Element => {

  const org_unit_id = platformUnit && platformUnit.id

  const [platformUnitDetail] = usePlatformUnit({
    org_unit_id,
  })

  const referenceDateParam = referenceDate && isoDateUTC(referenceDate)

  const [state, setState] = useContext(DashboardContext)

  const activeDimension = state.dimension as AnyPerformanceDimension

  const comparisonsByProvider = useComparisonsByProvider({
    org_unit_id,
    providers,
    target_currency: state.currency,
    ...(referenceDateParam && {
      reference_date: referenceDateParam,
    })
  })

  const [timeseriesByProvider] = usePerformanceTimeseriesByProvider({
    org_unit_id,
    period: 'DAYS_365',
    chunking: 'MONTH',
    target_currency: state.currency,
    ...(referenceDateParam && {
      reference_date: referenceDateParam,
    })
  })

  const datasets = useProviderDatasets({
    timeseriesByProvider: timeseriesByProvider.data,
    doubleVerifyTimeseriesByProvider: null,
    comparisonsByProvider: comparisonsByProvider.data,
    doubleVerifyComparisonsByProvider: null,
    platformUnit: platformUnitDetail.data,
    currency: state.currency,
  })

  const appTheme = useAppTheme()

  const channelSummaries = useChannelSummaries({
    appTheme,
    datasets,
  })

  const availableDimensions = useMemo(() => {
    if( platformUnitDetail.data ){
      return resolvePerformanceReportingDimensions({
        platformUnit: platformUnitDetail.data,
      })
    }
    return undefined
  }, [platformUnitDetail.data])

  useEffect(() => {
    if( availableDimensions && !availableDimensions.includes(activeDimension) ){
      setState({ dimension: availableDimensions[0] })
    }
  }, [availableDimensions, activeDimension, setState])

  const spendDatasets = useMemo(() => {
    return channelSummaries.map( ({ channel, provider, datasets }) => {
      return {
        key: provider || channel,
        label: provider ? providerLabelMap[provider] : channelDisplayMap[channel].label,
        provider,
        channel,
        dimension: activeDimension,
        color: appTheme.palette.channel[channel].main,
        dataset: {
          ...(datasets[activeDimension] || {}),
          channel,
          provider,
          dimension: activeDimension,
          relative: {
            ...(datasets[activeDimension] && datasets[activeDimension].relative),
            label: channel,
            channel,
            provider,
            dimension: activeDimension,
          }
        }
      }
    })
  }, [activeDimension, channelSummaries, appTheme])

  const loading = some([
    platformUnitDetail,
    timeseriesByProvider,
    comparisonsByProvider,
  ], isLoading)

  if( loading ){
    return <Loader preset='fullsize' minHeight='10rem' />
  }

  return (
    <Fragment>
      <DimensionalSummary
        dimension={activeDimension}
        color='neutral'
        currency={state.currency}
        referenceDate={referenceDate}
        datasets={spendDatasets}
        labelHistory='History'
        labelChange='Comparison'
        labelSplit='Breakdown'
        PerformanceTableProps={{
          renderers: cellRenderers as PerformanceTableProps<PerformanceReportingDatum>['renderers'],
        }} />

      <Box my={3}>
        <PerformanceTotals
          org_unit_id={platformUnit.id}
          dimension={activeDimension}
          currency={state.currency}
          providers={providers}
          referenceDate={referenceDate}
          potentialEfficiencyLabel={getPotentialEfficiencyLabel(platformUnit)} />
      </Box>
    </Fragment>
  )
}
